import { useQuery } from "@apollo/client";
import HeadsetMicOutlinedIcon from "@mui/icons-material/HeadsetMicOutlined";
import {
  Alert,
  Box,
  ButtonBase,
  CircularProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { Title, useCanAccess, useTranslate } from "react-admin";
import { useNavigate } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, Cell, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { CrmCard } from "../components/crm/CrmCard";
import { CrmDataTable } from "../components/crm/CrmDataTable";
import { CrmPageHeader } from "../components/crm/CrmPageHeader";
import { RequirePermission } from "../components/RequirePermission";
import { ResourceName, TELEPHONY_RESOURCE } from "../constants/resources";
import {
  CallKpiRange,
  CallListFilter,
  CrmFieldType,
  CrmObject,
  CustomerServiceCallKpisDocument,
  type CustomerServiceCallKpisQuery,
  CustomerServiceCrmOverviewDocument,
  type CustomerServiceCrmOverviewQuery,
} from "../generated/graphql";
import {
  CRM_COLORS,
  crmCallsPath,
  crmListPath,
  formatCallDuration,
  formatCrmCount,
  formatCrmDate,
  formatCrmDateTime,
  formatCrmPercent,
} from "../lib/crm";
import { SALESFORCE_UNITY_PALETTE } from "../lib/salesforceDashboard";

type Overview = CustomerServiceCrmOverviewQuery["customerServiceCrmOverview"];
type CountKey = "openCases" | "waitingCases" | "escalatedCases" | "outsideSlaCases" | "createdToday" | "closedToday";

// Each tile opens the case list view it counts.
const TILES: { key: CountKey; listView: string; accent?: string }[] = [
  { key: "openCases", listView: "open" },
  { key: "waitingCases", listView: "waiting" },
  { key: "escalatedCases", listView: "escalated", accent: CRM_COLORS.error },
  { key: "outsideSlaCases", listView: "outsideSla", accent: CRM_COLORS.warning },
  { key: "createdToday", listView: "all" },
  { key: "closedToday", listView: "closedToday", accent: CRM_COLORS.success },
];
const CHART_HEIGHT = 260;
const TOP_GROUPS = 8;
const OLDEST_COLUMNS = [
  { key: "status", type: CrmFieldType.Picklist },
  { key: "owner", type: CrmFieldType.Text },
  { key: "createdAt", type: CrmFieldType.Datetime },
];

const tileSx = (accent?: string) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "4px",
  p: "16px",
  backgroundColor: CRM_COLORS.cardBackground,
  border: `1px solid ${CRM_COLORS.cardBorder}`,
  borderTop: `3px solid ${accent ?? CRM_COLORS.brand}`,
  borderRadius: "4px",
  boxShadow: CRM_COLORS.cardShadow,
  textAlign: "left",
});

const TileContent = ({ label, value, hint }: { label: string; value: string; hint?: string }) => (
  <>
    <Typography sx={{ fontSize: "12px", color: CRM_COLORS.textWeak }}>{label}</Typography>
    <Typography sx={{ fontSize: "28px", fontWeight: 300, lineHeight: 1.1, color: CRM_COLORS.text }}>{value}</Typography>
    {hint && <Typography sx={{ fontSize: "12px", color: CRM_COLORS.label }}>{hint}</Typography>}
  </>
);

// A tile with a number; it opens the list behind the number when it has a target.
const Tile = ({
  label,
  value,
  hint,
  accent,
  onClick,
  testId,
}: {
  label: string;
  value: string;
  hint?: string;
  accent?: string;
  onClick?: () => void;
  testId?: string;
}) =>
  onClick ? (
    <ButtonBase
      data-testid={testId}
      onClick={onClick}
      sx={{ ...tileSx(accent), "&:hover": { backgroundColor: CRM_COLORS.rowHover } }}
    >
      <TileContent label={label} value={value} hint={hint} />
    </ButtonBase>
  ) : (
    <Box data-testid={testId} sx={tileSx(accent)}>
      <TileContent label={label} value={value} hint={hint} />
    </Box>
  );

const CountChart = ({ data, none }: { data: Overview["openByType"]; none: string }) => {
  const rows = data.slice(0, TOP_GROUPS).map((entry, index) => ({
    label: entry.label ?? none,
    count: entry.count,
    fill: SALESFORCE_UNITY_PALETTE[index % SALESFORCE_UNITY_PALETTE.length],
  }));
  return (
    <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
      <BarChart data={rows} layout="vertical" margin={{ left: 8, right: 24 }}>
        <CartesianGrid horizontal={false} stroke="rgba(197,211,223,0.5)" />
        <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
        <YAxis type="category" dataKey="label" width={150} tick={{ fontSize: 12 }} />
        <Tooltip />
        <Bar dataKey="count" isAnimationActive={false}>
          {rows.map(row => (
            <Cell key={row.label} fill={row.fill} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

const OverviewContent = ({ overview }: { overview: Overview }) => {
  const translate = useTranslate();
  const navigate = useNavigate();
  const t = (key: string) => translate(`crm.overview.${key}`);
  const calls = overview.callsPerDay.map(entry => ({
    day: formatCrmDate(entry.day).replace(/\s?\d{4}$/, ""),
    [t("inbound")]: entry.inbound,
    [t("outbound")]: entry.outbound,
  }));

  return (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(3, 1fr)", xl: "repeat(6, 1fr)" },
          gap: "12px",
          mb: "12px",
        }}
      >
        {TILES.map(tile => (
          <Tile
            key={tile.key}
            label={t(tile.key)}
            value={formatCrmCount(overview[tile.key])}
            accent={tile.accent}
            onClick={() => navigate(crmListPath(CrmObject.Case, tile.listView))}
          />
        ))}
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "repeat(3, 1fr)" }, gap: "12px", mb: "12px" }}>
        <CrmCard title={t("openByType")}>
          <CountChart data={overview.openByType} none={t("none")} />
        </CrmCard>
        <CrmCard title={t("openByOwner")}>
          <CountChart data={overview.openByOwner} none={t("none")} />
        </CrmCard>
        <CrmCard title={t("callsPerDay")}>
          <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
            <BarChart data={calls} margin={{ left: -16, right: 8 }}>
              <CartesianGrid vertical={false} stroke="rgba(197,211,223,0.5)" />
              <XAxis dataKey="day" tick={{ fontSize: 11 }} interval={1} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar
                dataKey={t("inbound")}
                stackId="calls"
                fill={SALESFORCE_UNITY_PALETTE[0]}
                isAnimationActive={false}
              />
              <Bar
                dataKey={t("outbound")}
                stackId="calls"
                fill={SALESFORCE_UNITY_PALETTE[4]}
                isAnimationActive={false}
              />
            </BarChart>
          </ResponsiveContainer>
        </CrmCard>
      </Box>
      <CrmCard flush title={t("oldestOpen")}>
        {overview.oldestOpen.length === 0 ? (
          <Typography sx={{ px: "16px", pb: "12px", fontSize: "13px" }}>{t("noCases")}</Typography>
        ) : (
          <CrmDataTable
            object={CrmObject.Case}
            columns={[{ key: "caseNumber", type: CrmFieldType.Text }, ...OLDEST_COLUMNS]}
            rows={overview.oldestOpen.map(row => ({
              ...row,
              values: [
                {
                  key: "caseNumber",
                  type: CrmFieldType.Text,
                  value: row.title,
                  referenceObject: null,
                  referenceTitle: null,
                },
                ...row.values,
              ],
            }))}
            dense
          />
        )}
      </CrmCard>
    </>
  );
};

type CallKpis = NonNullable<CustomerServiceCallKpisQuery["customerServiceCallKpis"]>;

const CALL_KPI_RANGES = [CallKpiRange.Today, CallKpiRange.Last_7Days, CallKpiRange.Last_30Days];

// A duration in seconds as m:ss; a dash while there is none.
const durationOrDash = (seconds: number | null | undefined, none: string): string =>
  seconds === null || seconds === undefined ? none : formatCallDuration(seconds);

// navigable is false while the counts shown belong to another range than the one selected.
const TelephonyContent = ({ kpis, range, navigable }: { kpis: CallKpis; range: CallKpiRange; navigable: boolean }) => {
  const translate = useTranslate();
  const navigate = useNavigate();
  const t = (key: string) => translate(`crm.telephony.${key}`);
  const none = t("none");
  const openCalls = (filter: CallListFilter, agentKey?: string) =>
    navigable ? () => navigate(crmCallsPath(range, filter, agentKey)) : undefined;
  const countTiles: { key: string; filter: CallListFilter; value: number; accent?: string }[] = [
    { key: "inbound", filter: CallListFilter.Inbound, value: kpis.inbound },
    { key: "answered", filter: CallListFilter.Answered, value: kpis.answered, accent: CRM_COLORS.success },
    { key: "missed", filter: CallListFilter.Missed, value: kpis.missed, accent: CRM_COLORS.warning },
    { key: "abandoned", filter: CallListFilter.Abandoned, value: kpis.abandoned, accent: CRM_COLORS.warning },
    { key: "notRouted", filter: CallListFilter.NotRouted, value: kpis.notRouted },
  ];

  return (
    <>
      {!kpis.hotlineConfigured && (
        <Alert severity="info" sx={{ mb: "12px" }}>
          {t("hotlineNotConfigured")}
        </Alert>
      )}
      {kpis.syncError && (
        <Alert severity="warning" sx={{ mb: "12px" }}>
          {t("syncError")}
        </Alert>
      )}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(3, 1fr)", xl: "repeat(5, 1fr)" },
          gap: "12px",
          mb: "12px",
        }}
      >
        {countTiles.map(tile => (
          <Tile
            key={tile.key}
            testId={`call-kpi-${tile.key}`}
            label={t(tile.key)}
            value={formatCrmCount(tile.value)}
            accent={tile.accent}
            onClick={openCalls(tile.filter)}
          />
        ))}
        <Tile
          testId="call-kpi-reachability"
          label={t("reachability")}
          value={typeof kpis.reachability === "number" ? formatCrmPercent(kpis.reachability) : none}
          hint={t("reachabilityHint")}
          onClick={openCalls(CallListFilter.WithinOpeningHours)}
        />
        <Tile
          testId="call-kpi-averageWait"
          label={t("averageWait")}
          value={durationOrDash(kpis.averageWaitSeconds, none)}
        />
        <Tile testId="call-kpi-maxWait" label={t("maxWait")} value={durationOrDash(kpis.maxWaitSeconds, none)} />
        <Tile
          testId="call-kpi-averageHandle"
          label={t("averageHandle")}
          value={durationOrDash(kpis.averageHandleSeconds, none)}
        />
        <Tile
          testId="call-kpi-outbound"
          label={t("outbound")}
          value={formatCrmCount(kpis.outbound)}
          onClick={openCalls(CallListFilter.Outbound)}
        />
      </Box>
      {kpis.agents.length > 0 && (
        <CrmCard flush title={t("agents")}>
          <Table size="small" aria-label={t("agents")}>
            <TableHead>
              <TableRow>
                <TableCell>{t("agentName")}</TableCell>
                <TableCell align="right">{t("answered")}</TableCell>
                <TableCell align="right">{t("outbound")}</TableCell>
                <TableCell align="right">{t("averageHandle")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {kpis.agents.map(agent => (
                <TableRow key={agent.agentKey} data-testid={`call-kpi-agent-${agent.agentKey}`}>
                  <TableCell>{agent.name}</TableCell>
                  <TableCell align="right">
                    <Link
                      component="button"
                      disabled={!navigable}
                      onClick={openCalls(CallListFilter.Answered, agent.agentKey)}
                    >
                      {formatCrmCount(agent.answered)}
                    </Link>
                  </TableCell>
                  <TableCell align="right">
                    <Link
                      component="button"
                      disabled={!navigable}
                      onClick={openCalls(CallListFilter.Outbound, agent.agentKey)}
                    >
                      {formatCrmCount(agent.outbound)}
                    </Link>
                  </TableCell>
                  <TableCell align="right">{durationOrDash(agent.averageHandleSeconds, none)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CrmCard>
      )}
    </>
  );
};

// The hotline's call counts; nothing while the Placetel integration is missing or off (the query returns null).
const TelephonySection = () => {
  const translate = useTranslate();
  const [range, setRange] = useState<CallKpiRange>(CallKpiRange.Today);
  const { data, previousData, loading, error } = useQuery<CustomerServiceCallKpisQuery>(
    CustomerServiceCallKpisDocument,
    {
      variables: { range },
      fetchPolicy: "cache-and-network",
    }
  );
  const kpis = (data ?? previousData)?.customerServiceCallKpis;

  if (!kpis && !error) {
    return loading ? <CircularProgress aria-label={translate("crm.telephony.loading")} /> : null;
  }
  return (
    <Box
      component="section"
      data-testid="crm-telephony"
      aria-label={translate("crm.telephony.title")}
      sx={{ mt: "12px" }}
    >
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "12px", mb: "12px" }}>
        <Typography component="h2" sx={{ fontSize: "16px", fontWeight: 700, color: CRM_COLORS.text }}>
          {translate("crm.telephony.title")}
        </Typography>
        <ToggleButtonGroup
          aria-label={translate("crm.telephony.range")}
          exclusive
          onChange={(_event, next: CallKpiRange | null) => next && setRange(next)}
          size="small"
          value={range}
        >
          {CALL_KPI_RANGES.map(candidate => (
            <ToggleButton key={candidate} data-testid={`call-kpi-range-${candidate}`} value={candidate}>
              {translate(`crm.telephony.ranges.${candidate}`)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        {!error && kpis?.syncedAt && (
          <Typography sx={{ fontSize: "12px", color: CRM_COLORS.label }}>
            {translate("crm.telephony.completeUntil", { time: formatCrmDateTime(kpis.syncedAt) })}
          </Typography>
        )}
      </Box>
      {error ? (
        <Alert severity="error">{translate("crm.telephony.loadError")}</Alert>
      ) : (
        kpis && (
          <Box sx={{ opacity: loading ? 0.5 : 1 }}>
            <TelephonyContent kpis={kpis} range={range} navigable={Boolean(data)} />
          </Box>
        )
      )}
    </Box>
  );
};

const CrmHome = () => {
  const translate = useTranslate();
  const { canAccess: mayPhone } = useCanAccess({ action: "list", resource: TELEPHONY_RESOURCE });
  const { data, loading, error } = useQuery<CustomerServiceCrmOverviewQuery>(CustomerServiceCrmOverviewDocument, {
    fetchPolicy: "cache-and-network",
  });
  const overview = data?.customerServiceCrmOverview;
  return (
    <Box data-testid="crm-home">
      <Title title={translate("crm.overview.title")} />
      <CrmPageHeader
        icon={
          <Box
            aria-hidden
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 40,
              height: 40,
              borderRadius: "4px",
              backgroundColor: CRM_COLORS.brand,
              color: CRM_COLORS.iconForeground,
            }}
          >
            <HeadsetMicOutlinedIcon />
          </Box>
        }
        overline={translate("crm.overview.overline")}
        title={translate("crm.overview.title")}
        meta={overview?.syncedAt && translate("crm.syncedAt", { time: formatCrmDateTime(overview.syncedAt) })}
      />
      {error && <Alert severity="error">{translate("crm.loadError")}</Alert>}
      {loading && !overview && <CircularProgress aria-label={translate("crm.loading")} />}
      {overview && <OverviewContent overview={overview} />}
      {mayPhone && <TelephonySection />}
    </Box>
  );
};

export const CrmHomePage = () => (
  <RequirePermission permission={ResourceName.Crm}>
    <CrmHome />
  </RequirePermission>
);
